import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { PreventTransitionPrompt, useDialogs } from '@michelin/acid-components';
import { useMutation, useQueryClient } from 'react-query';
import { GenericActionButtonsMassUpdate } from 'components/GenericActionButtons/GenericActionButtonsMassUpdate'; import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { saveCommercialNetworkServices } from 'hooks/useCommercialServices';
import { CommercialOverrides } from 'components/CommercialOverrides/CommercialOverridesFields';
import { Warning } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { CheckboxGroup } from './CheckboxGroup';
import { commercialNetworkServicesLabels, CommercialNetworkServices } from './CommercialServicesFields';

interface CommercialNetworksPanelProps {
  networks: CommercialNetworkServices
  action: 'edit' | 'view' | 'add',
  commercialOverrides?: CommercialOverrides,
  fromMassUpdate?: boolean,
}

export default function CommercialNetworksPanel({
  networks, action, fromMassUpdate, commercialOverrides,
}: CommercialNetworksPanelProps) {
  const { t } = useTranslation();
  const title = t('Networks');
  const { stNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const history = useHistory();
  const permissions = usePermissionsSPP();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formState, getValues, reset, control, watch, setValue, handleSubmit,
  } = useForm({ defaultValues: networks });
  const allowsEdit = permissions.allowsAction('commercial_networks.update_tire_care_member')
                  || permissions.allowsAction('commercial_networks.update_mechanical_care_dealer');

  const queryClient = useQueryClient();
  const saveMutation = useMutation(saveCommercialNetworkServices, {
    onSuccess: () => queryClient.invalidateQueries(['commercial-networks', stNumber]),
  });
  const { errorDialog } = useDialogs();
  const readOnly: boolean = action === 'view';

  useEffect(() => {
    if (action === 'view') reset();
  }, [action, reset]);

  useEffect(() => {
    reset(networks);
  }, [networks, reset]);

  const save = async () => {
    if (!allowsEdit) return;
    try {
      if (!formState.isDirty) return;
      const data = getValues() as any;
      delete data.service_ranking_actual;
      delete data.mcsn;
      await saveMutation.mutateAsync({ stNumber, data });
      reset(getValues());
      history.push('view');
      enqueueSnackbar(t('Commercial Network information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (error) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Commercial Products Data.'),
      );
    }
  };

  return (
    <>
      {fromMassUpdate && allowsEdit && (
        <GenericActionButtonsMassUpdate
          isDirty={formState.isDirty}
          getValues={getValues}
          reset={reset}
          handleSubmit={handleSubmit}
          validateAction="mass_st_update.update"
        />
      )}
      {!fromMassUpdate && (
        <GenericActionButtons
          isDirty={formState.isDirty}
          isSaving={saveMutation.isLoading}
          onSave={save}
          preventEdition={!allowsEdit}
          validateAction="commercial_networks.read"
          readOnly={readOnly}
        />
      )}
      <CheckboxGroup
        getValues={getValues}
        title={title}
        control={control}
        readOnly={readOnly}
        cols={12}
        labels={commercialNetworkServicesLabels}
        setValue={setValue}
        watch={watch}
        items={[
          {
            key: 'mcsn',
            disabled: true,
            onRender: () => {
              if (!commercialOverrides?.overrides.mcsn_member) return null;
              if (commercialOverrides.overrides_original.mcsn_member === commercialOverrides.overrides_value.mcsn_member) return null;
              return (
                <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}>
                  <Warning style={{ height: 20, verticalAlign: 'middle', marginRight: 4 }} />
                  {t('This value is being overwritten')}
                </span>
              );
            },
          },
        ]}
      />
      <PreventTransitionPrompt
        when={formState.isDirty}
      />
    </>
  );
}
